import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Outlet, useNavigate, useSearchParams } from "react-router";
import { useShallow } from "zustand/react/shallow";
import { WORLD_MANIFEST } from "@townsy/world";
import { errorMessage } from "../api/client";
import { useBootstrapQuery, usePropertiesQuery } from "../api/queries";
import { queryClient } from "../api/queryClient";
import { Joystick } from "../input/Joystick";
import { RunToggle } from "../input/RunToggle";
import { WalkKeysHint } from "../input/WalkKeysHint";
import { isTouchDevice, no3dRequested, onReducedMotionChange, prefersReducedMotion, webglSupported } from "../lib/env";
import { useTownStore } from "../state/store";
import { ChatPanel } from "../ui/ChatPanel";
import { ConnectionBanner } from "../ui/ConnectionBanner";
import { ControlTakenDialog } from "../ui/ControlTakenDialog";
import { DirectoryPanel } from "../ui/DirectoryPanel";
import { EnterPrompt } from "../ui/EnterPrompt";
import { Hud } from "../ui/Hud";
import { LoadingOverlay } from "../ui/LoadingOverlay";
import { NoticeBar } from "../ui/NoticeBar";
import { DeletionNotice } from "../ui/privacy/PrivacySection";
import { PropertyPanel } from "../ui/PropertyPanel";
import { HomeShopPanel } from "../ui/homes/HomeShopPanel";
import { MyHomePanel } from "../ui/homes/MyHomePanel";
import { RealEstatePanel } from "../ui/realEstate/RealEstatePanel";
import { VenueHudHost } from "../venues/VenueHudHost";
import { ReportDialog } from "../ui/ReportDialog";
import { PersonCard } from "../ui/people/PersonCard";
import { PersonReportDialog } from "../ui/people/PersonReportDialog";
import { MessagesHost } from "../ui/messages/MessagesDialog";
import { WorldsHost } from "../ui/worlds/WorldsDialog";
import { loadMutes } from "../safety/mutes";
import { StatsPanel } from "../ui/StatsPanel";
import { Toasts } from "../ui/Toasts";
import { WalkInvite } from "../ui/WalkInvite";
import { WalletPanel } from "../ui/WalletPanel";
import { CheckoutHost } from "../ui/checkout/CheckoutHost";
import { Minimap, selectMinimapVisible } from "../ui/minimap/Minimap";
import { StoreThemeHost } from "../ui/StoreThemeHost";
import { PlacementHost } from "../ui/advertising/PlacementDialog";
import { StoreSetupHost } from "../ui/storeSetup/StoreSetupDialog";
import { OfferSetupHost } from "../ui/rewards/offers/OfferSetupDialog";
import { TaskHost } from "../ui/rewards/AttemptDialog";
import { EarnCoinsPanel } from "../ui/rewards/EarnCoins";
import { StoreTasksPanel } from "../ui/rewards/StoreTasks";
import { FallbackDirectory } from "./FallbackDirectory";
import { TownSessionContext } from "./TownSessionContext";
import { TownSession } from "./townSession";
import { useBillboardMirror } from "./useBillboardMirror";

/**
 * The town screen: canvas + HUD + panels. Owns the TownSession lifecycle (one per visitor
 * identity) and keeps it alive while the child route switches between `/` and `/place/:id`.
 */
export function TownLayout() {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const bootstrapQ = useBootstrapQuery();
  const propertiesQ = usePropertiesQuery(Boolean(bootstrapQ.data));
  /** `/?district=<id>` as the page was opened (the URL loses it right after; see below). */
  const arrivalDistrictRef = useRef<string | null>(typeof window === "undefined" ? null : new URLSearchParams(window.location.search).get("district"));
  const bootstrapRef = useRef(bootstrapQ.data);
  bootstrapRef.current = bootstrapQ.data;

  const { setBootstrap, setProperties, setUi, selectProperty, toggleStats, toggleDirectory, toggleChat } = useTownStore(
    useShallow((s) => ({
      setBootstrap: s.setBootstrap,
      setProperties: s.setProperties,
      setUi: s.setUi,
      selectProperty: s.selectProperty,
      toggleStats: s.toggleStats,
      toggleDirectory: s.toggleDirectory,
      toggleChat: s.toggleChat,
    })),
  );
  const { sceneStatus, sceneError, roomStatus, isTouch, mode, selected, driving, runToggle, districtId } = useTownStore(
    useShallow((s) => ({
      sceneStatus: s.ui.sceneStatus,
      sceneError: s.ui.sceneError,
      roomStatus: s.room.status,
      isTouch: s.ui.isTouch,
      mode: s.ui.mode,
      selected: s.ui.selectedPropertyId,
      driving: s.ui.drivingVehicleId !== null,
      runToggle: s.ui.runToggle,
      districtId: s.districtId,
    })),
  );
  const properties = useTownStore((s) => s.properties);
  const minimapShown = useTownStore(selectMinimapVisible);

  const fallback = useMemo<"requested" | "webgl" | null>(() => (no3dRequested() ? "requested" : webglSupported() ? null : "webgl"), []);
  const [session, setSession] = useState<TownSession | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const actorId = bootstrapQ.data?.actor.actorId ?? null;

  useEffect(() => {
    setUi({ reducedMotion: prefersReducedMotion(), isTouch: isTouchDevice() });
  }, [setUi]);

  useEffect(() => {
    if (bootstrapQ.data) setBootstrap(bootstrapQ.data);
  }, [bootstrapQ.data, setBootstrap]);

  // Mutes for this identity (ADR-0013): the account's from the server, a guest's from this browser.
  const hasBootstrap = Boolean(bootstrapQ.data);
  const accountUserId = bootstrapQ.data?.session.userId ?? null;
  useEffect(() => {
    if (hasBootstrap) void loadMutes(Boolean(accountUserId));
  }, [hasBootstrap, accountUserId]);

  useEffect(() => {
    if (propertiesQ.data) setProperties(propertiesQ.data.items, propertiesQ.data.worldVersion);
  }, [propertiesQ.data, setProperties]);

  // One session per visitor identity (re-created after sign in / sign out changes the actor).
  useEffect(() => {
    const bootstrap = bootstrapRef.current;
    if (!bootstrap || !actorId || fallback) return;
    if (bootstrap.worldVersion !== WORLD_MANIFEST.version) {
      useTownStore.getState().pushToast("A newer town layout is available. Reload to get it.", "warning");
    }
    const s = new TownSession({
      bootstrap,
      manifest: WORLD_MANIFEST,
      queryClient,
      navigate: (path) => navigateRef.current(path),
      // `/?district=<id>`: open the town on that district's square (the first session only; unknown ids are ignored).
      arrivalDistrictId: arrivalDistrictRef.current,
    });
    arrivalDistrictRef.current = null;
    setSession(s);
    const canvas = canvasRef.current;
    // The room is joined once the town is built (or failed to build): the build is the heaviest main-thread work of a
    // load, and a socket the page cannot read meanwhile stops answering pings and is dropped by the server (a slow
    // device; a software GPU shared with other pages took 30-47 s for town v6, past the 30 s reconnection grace).
    if (canvas) void s.startScene(canvas).finally(() => void s.connect());
    else void s.connect();
    const offMotion = onReducedMotionChange((reduced) => s.setReducedMotion(reduced));
    return () => {
      offMotion();
      s.dispose();
      setSession(null);
    };
  }, [actorId, fallback, retryKey]);

  useEffect(() => {
    session?.updatePlacements();
  }, [session, properties]);

  // What the billboards show today (ADR-0009 #9): on load, after every UTC midnight and on billboard notifications.
  useBillboardMirror(session, Boolean(bootstrapQ.data) && !fallback);

  // `/?myhome=1` (account page, "Customize my home" links): open the My home manager once.
  const [search, setSearch] = useSearchParams();
  // `/?district=<id>` (a link to a district's square): used by the first street join, then dropped from the address so
  // a reload returns to where the visitor was (ADR-0042 decision 5) rather than to the link's district.
  const wantsDistrict = search.get("district");
  useEffect(() => {
    if (!wantsDistrict) return;
    const next = new URLSearchParams(search);
    next.delete("district");
    setSearch(next, { replace: true });
  }, [wantsDistrict, search, setSearch]);
  const wantsMyHome = search.get("myhome");
  useEffect(() => {
    if (!wantsMyHome) return;
    setUi({ myHomeOpen: true, myHomeTab: wantsMyHome === "inside" ? "interior" : "yard" });
    const next = new URLSearchParams(search);
    next.delete("myhome");
    setSearch(next, { replace: true });
  }, [wantsMyHome, search, setSearch, setUi]);

  useEffect(() => {
    if (sceneStatus === "ready") session?.focusProperty(selected);
  }, [session, selected, sceneStatus]);

  // Escape closes the topmost UI layer; dialogs handle their own Escape through the focus trap.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Enter opens room chat (or puts the cursor in it) when nothing else is being typed in or shown in a dialog.
      if (e.key === "Enter" && !e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey) {
        const t = e.target as HTMLElement | null;
        const typing = t?.closest?.("input, textarea, select, [contenteditable='true'], button, a") ?? null;
        const st = useTownStore.getState();
        if (typing || document.querySelector('[role="dialog"]') || st.room.status !== "joined") return;
        e.preventDefault();
        if (!st.ui.chatOpen) toggleChat(true);
        else document.querySelector<HTMLTextAreaElement>(".chat textarea")?.focus();
        return;
      }
      if (e.key !== "Escape") return;
      const s = useTownStore.getState();
      if (s.ui.reportOpen || s.ui.statsOpen || s.ui.directoryOpen || s.ui.walletOpen || s.ui.earnOpen || s.task || s.roomProblem?.state === "control_taken") return;
      if (document.querySelector('[role="dialog"]')) return; // purchase / board dialogs close themselves
      if (s.ui.menuOpen) return;
      if (s.ui.chatOpen && !s.ui.chatFocused) {
        toggleChat(false);
        return;
      }
      if (s.ui.selectedActorId) {
        s.selectActor(null);
        return;
      }
      if (s.ui.selectedPropertyId) {
        navigateRef.current("/");
        return;
      }
      if (s.ui.myHomeOpen) {
        setUi({ myHomeOpen: false });
        return;
      }
      if (s.ui.storeTasksFor) {
        setUi({ storeTasksFor: null });
        return;
      }
      if (s.ui.realEstateOpen) setUi({ realEstateOpen: false });
      else if (s.ui.homeBuildersOpen) setUi({ homeBuildersOpen: false });
      else if (s.ui.furnishingsOpen) setUi({ furnishingsOpen: false });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggleChat, toggleStats, toggleDirectory, selectProperty, setUi]);

  if (fallback) return <FallbackDirectory reason={fallback} />;

  const failed = sceneStatus === "failed";
  let overlay: ReactNode = null;
  if (bootstrapQ.isPending) {
    overlay = <LoadingOverlay stage="Preparing your visitor pass…" />;
  } else if (bootstrapQ.error) {
    overlay = <LoadingOverlay stage="" error={errorMessage(bootstrapQ.error)} onRetry={() => void bootstrapQ.refetch()} />;
  } else if (!failed && (sceneStatus === "idle" || sceneStatus === "loading")) {
    overlay = <LoadingOverlay stage="Building the town…" hint="Loading the 3D renderer. The directory works without it." />;
  } else if (!failed && roomStatus === "connecting") {
    // The first join lands at the start district's square (town v6: one street; Central: Main Street).
    const first = WORLD_MANIFEST.districts[0]!;
    const where = districtId === first.id ? "Main Street" : (WORLD_MANIFEST.districts.find((d) => d.id === districtId)?.name ?? "the town");
    overlay = <LoadingOverlay stage={`Joining ${where}…`} hint="Getting a room pass from the server." />;
  }

  return (
    <TownSessionContext.Provider value={session}>
      <div className={minimapShown && !failed ? "town-root has-minimap" : "town-root"} style={failed ? { overflow: "auto" } : undefined}>
        <canvas
          ref={canvasRef}
          className="town-canvas"
          tabIndex={-1}
          aria-label="Townsy 3D town. Use the Directory for a text version of every listing."
          style={failed ? { display: "none" } : undefined}
        />
        {failed ? (
          <FallbackDirectory
            reason="failed"
            error={sceneError}
            onRetry={() => {
              setUi({ sceneStatus: "idle", sceneError: null });
              setRetryKey((k) => k + 1);
            }}
          />
        ) : (
          <>
            {bootstrapQ.data ? (
              <Hud
                below={
                  <>
                    <NoticeBar />
                    <DeletionNotice signedIn={Boolean(bootstrapQ.data.session.userId)} compact />
                  </>
                }
              />
            ) : null}
            <Outlet />
            <RealEstatePanel />
            <VenueHudHost />
            <HomeShopPanel shop="home_builders" />
            <HomeShopPanel shop="furnishings" />
            <MyHomePanel />
            <PropertyPanel />
            <StoreTasksPanel />
            <ChatPanel />
            <Minimap />
            <EnterPrompt />
            <WalkInvite />
            {isTouch && mode === "walk" && session && sceneStatus === "ready" ? <Joystick driving={driving} onChange={(v) => session.input.setJoystick(v)} /> : null}
            {isTouch && mode === "walk" && session && sceneStatus === "ready" && !driving ? <RunToggle on={runToggle} onChange={(on) => session.setRunToggle(on)} /> : null}
            {!isTouch && mode === "walk" && session && sceneStatus === "ready" && !driving ? <WalkKeysHint /> : null}
            <StatsPanel />
            <DirectoryPanel />
            <WalletPanel />
            <EarnCoinsPanel />
            <TaskHost />
            <CheckoutHost />
            <StoreThemeHost />
            <PlacementHost />
            <StoreSetupHost />
            <OfferSetupHost />
            <ReportDialog />
            <PersonCard />
            <MessagesHost />
            <WorldsHost />
            <PersonReportDialog />
            <ControlTakenDialog />
            <ConnectionBanner />
            <Toasts />
            {overlay}
          </>
        )}
      </div>
    </TownSessionContext.Provider>
  );
}
